// ============================================================================
// Shopee — Orders (get_order_list + get_order_detail).
//
// Shopee splits order fetching into two calls:
//   1. `/api/v2/order/get_order_list` — paginated list of order_sn + status.
//      Supports `order_status` filter (ONE status per request), `time_from`/
//      `time_to` window (MAX 15 DAYS — Shopee's hard cap), cursor pagination.
//   2. `/api/v2/order/get_order_detail` — batch fetch of FULL order details
//      by order_sn. MAX 50 order_sn per call.
//
// 15-day window constraint:
//   If `updatedSince` is > 15 days ago, we fan out into multiple 15-day
//   windows walking forward until we hit the current time. The capability
//   layer's `fetchOrders` handles the single-window case; full historical
//   sync is a scheduler job concern (out of scope here) — we just keep
//   `listShopeeOrders` honest about ONE request == ONE 15-day window max.
// ============================================================================

import type {
  CommerceOrder,
  CommerceOrderItem,
  CommerceOrderStatus,
  CommerceShippingAddress,
} from '../../capabilities/commerce'
import { shopeeApiCall } from './client'
import type {
  ShopeeOrderDetailResponse,
  ShopeeOrderListResponse,
  ShopeeOrderRaw,
} from './types'
import { canonicalToShopeeStatus, mapShopeeOrderStatus, ShopeeError } from './types'

// ============================================================================
// Constants
// ============================================================================

/** Shopee's hard cap on get_order_detail batch size. */
export const SHOPEE_ORDER_BATCH_MAX = 50

/** Shopee's hard cap on get_order_list time window. */
export const SHOPEE_ORDER_WINDOW_MAX_MS = 15 * 24 * 60 * 60 * 1000

/** Default page size for get_order_list. */
export const SHOPEE_ORDER_PAGE_DEFAULT = 50

// ============================================================================
// Auth context (shared with products.ts)
// ============================================================================

export interface ShopeeAuthContext {
  partnerId: string | number
  partnerKey: string
  accessToken: string
  shopId: string | number
  apiBase?: string
  fetchImpl?: typeof fetch
}

// ============================================================================
// listShopeeOrders — cursor-paginated order list within a 15-day window
// ============================================================================

export interface ListShopeeOrdersInput extends ShopeeAuthContext {
  /** Start of the window (epoch seconds). */
  timeFrom: number
  /** End of the window (epoch seconds). MUST be within 15 days of `timeFrom`. */
  timeTo: number
  /**
   * Filter by ONE Shopee status string. Shopee does NOT accept a list —
   * callers with multiple statuses fan out at the capability layer.
   */
  orderStatus?: string
  /** Opaque cursor from a prior response. */
  cursor?: string
  /** Page size — Shopee caps at 100. */
  pageSize?: number
}

export interface ListShopeeOrdersOutput {
  orderSnList: string[]
  more: boolean
  nextCursor?: string
}

export async function listShopeeOrders(
  input: ListShopeeOrdersInput,
): Promise<ListShopeeOrdersOutput> {
  if (!Number.isFinite(input.timeFrom) || !Number.isFinite(input.timeTo)) {
    throw new ShopeeError('INVALID_INPUT', 'timeFrom + timeTo must be epoch seconds')
  }
  if (input.timeTo < input.timeFrom) {
    throw new ShopeeError('INVALID_INPUT', 'timeTo must be >= timeFrom')
  }
  const windowMs = (input.timeTo - input.timeFrom) * 1000
  if (windowMs > SHOPEE_ORDER_WINDOW_MAX_MS) {
    throw new ShopeeError(
      'INVALID_INPUT',
      `Shopee order_list window is capped at 15 days; received ${Math.round(windowMs / 86400000)} days`,
    )
  }

  const pageSize =
    typeof input.pageSize === 'number' && input.pageSize > 0
      ? Math.min(100, Math.floor(input.pageSize))
      : SHOPEE_ORDER_PAGE_DEFAULT

  const query: Record<string, string | number | undefined> = {
    time_range_field: 'update_time',
    time_from: Math.floor(input.timeFrom),
    time_to: Math.floor(input.timeTo),
    page_size: pageSize,
  }
  if (input.orderStatus) query.order_status = input.orderStatus
  if (input.cursor) query.cursor = input.cursor

  const apiInput: Parameters<typeof shopeeApiCall<ShopeeOrderListResponse>>[0] = {
    apiPath: '/api/v2/order/get_order_list',
    method: 'GET',
    partnerId: input.partnerId,
    partnerKey: input.partnerKey,
    accessToken: input.accessToken,
    shopId: input.shopId,
    query,
    errorHint: 'order',
    ...(input.apiBase ? { apiBase: input.apiBase } : {}),
    ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
  }
  const resp = await shopeeApiCall<ShopeeOrderListResponse>(apiInput)
  const list = resp.response?.order_list ?? []
  return {
    orderSnList: list.map((o) => o.order_sn).filter((s): s is string => typeof s === 'string'),
    more: Boolean(resp.response?.more),
    nextCursor:
      typeof resp.response?.next_cursor === 'string' ? resp.response.next_cursor : undefined,
  }
}

/** Canonical status → Shopee wire-level status string. Re-exported for convenience. */
export { canonicalToShopeeStatus }

// ============================================================================
// getShopeeOrderDetail — batched full-detail fetch
// ============================================================================

export interface GetShopeeOrderDetailInput extends ShopeeAuthContext {
  /** The order_sn values to fetch. Auto-split into batches of ≤ 50. */
  orderSnList: string[]
}

/**
 * Fetch full order details for an arbitrary list of `order_sn` values.
 *
 * **⚠️ SILENT AUTO-SPLIT (Issue #255 M1):** when `orderSnList.length > 50`
 * this helper transparently splits into multiple sequential API calls. Every
 * batch consumes one Shopee quota unit. Budget accordingly — if the caller
 * needs concurrency or a hard batch cap, slice upstream.
 */
export async function getShopeeOrderDetail(
  input: GetShopeeOrderDetailInput,
): Promise<ShopeeOrderRaw[]> {
  if (!Array.isArray(input.orderSnList)) {
    throw new ShopeeError('INVALID_INPUT', 'orderSnList must be an array')
  }
  if (input.orderSnList.length === 0) return []

  const deduped = Array.from(new Set(input.orderSnList.filter((s) => typeof s === 'string')))
  const batches: string[][] = []
  for (let i = 0; i < deduped.length; i += SHOPEE_ORDER_BATCH_MAX) {
    batches.push(deduped.slice(i, i + SHOPEE_ORDER_BATCH_MAX))
  }

  const all: ShopeeOrderRaw[] = []
  for (const batch of batches) {
    const apiInput: Parameters<typeof shopeeApiCall<ShopeeOrderDetailResponse>>[0] = {
      apiPath: '/api/v2/order/get_order_detail',
      method: 'GET',
      partnerId: input.partnerId,
      partnerKey: input.partnerKey,
      accessToken: input.accessToken,
      shopId: input.shopId,
      query: {
        order_sn_list: batch.join(','),
        // Recipient address + item_list are opt-in via `response_optional_fields`.
        response_optional_fields: 'buyer_username,recipient_address,item_list,pay_time',
      },
      errorHint: 'order',
      ...(input.apiBase ? { apiBase: input.apiBase } : {}),
      ...(input.fetchImpl ? { fetchImpl: input.fetchImpl } : {}),
    }
    const resp = await shopeeApiCall<ShopeeOrderDetailResponse>(apiInput)
    const list = resp.response?.order_list ?? []
    all.push(...list)
  }
  return all
}

// ============================================================================
// shopeeOrdersToCommerce — raw Shopee shape → CommerceOrder[]
// ============================================================================

export function shopeeOrdersToCommerce(rawOrders: ShopeeOrderRaw[]): CommerceOrder[] {
  if (!Array.isArray(rawOrders)) return []
  const out: CommerceOrder[] = []
  for (const raw of rawOrders) {
    if (!raw || typeof raw.order_sn !== 'string') continue
    out.push(shopeeOrderToCommerce(raw))
  }
  return out
}

function shopeeOrderToCommerce(raw: ShopeeOrderRaw): CommerceOrder {
  const status: CommerceOrderStatus = mapShopeeOrderStatus(raw.order_status)
  const currency = typeof raw.currency === 'string' ? raw.currency : 'THB'

  const items: CommerceOrderItem[] = Array.isArray(raw.item_list)
    ? raw.item_list.map((i) => {
        const item: CommerceOrderItem = {
          productId: typeof i.item_id === 'number' ? String(i.item_id) : '',
          name: typeof i.item_name === 'string' ? i.item_name : '',
          quantity: typeof i.model_quantity_purchased === 'number' ? i.model_quantity_purchased : 0,
          unitPrice:
            typeof i.model_discounted_price === 'number'
              ? i.model_discounted_price
              : typeof i.model_original_price === 'number'
                ? i.model_original_price
                : 0,
        }
        if (typeof i.model_id === 'number' && i.model_id > 0) {
          item.variantId = String(i.model_id)
        }
        const sku = i.model_sku ?? i.item_sku
        if (typeof sku === 'string' && sku.length > 0) item.sku = sku
        return item
      })
    : []

  const order: CommerceOrder = {
    id: raw.order_sn,
    status,
    items,
    totalPrice: typeof raw.total_amount === 'number' ? raw.total_amount : 0,
    currency,
    createdAt:
      typeof raw.create_time === 'number' ? new Date(raw.create_time * 1000).toISOString() : '',
    updatedAt:
      typeof raw.update_time === 'number' ? new Date(raw.update_time * 1000).toISOString() : '',
  }
  if (typeof raw.buyer_username === 'string' && raw.buyer_username.length > 0) {
    order.buyerName = raw.buyer_username
  }
  if (typeof raw.tracking_number === 'string' && raw.tracking_number.length > 0) {
    order.trackingNumber = raw.tracking_number
  }
  if (typeof raw.pay_time === 'number' && raw.pay_time > 0) {
    order.paidAt = new Date(raw.pay_time * 1000).toISOString()
  }
  if (raw.recipient_address && typeof raw.recipient_address === 'object') {
    order.shippingAddress = shopeeAddressToCommerce(raw.recipient_address)
  }
  return order
}

function shopeeAddressToCommerce(
  addr: NonNullable<ShopeeOrderRaw['recipient_address']>,
): CommerceShippingAddress {
  const out: CommerceShippingAddress = {
    name: typeof addr.name === 'string' ? addr.name : '',
    line1: typeof addr.full_address === 'string' ? addr.full_address : '',
  }
  if (typeof addr.phone === 'string' && addr.phone.length > 0) out.phone = addr.phone
  if (typeof addr.town === 'string' && addr.town.length > 0) out.line2 = addr.town
  if (typeof addr.district === 'string' && addr.district.length > 0) out.district = addr.district
  if (typeof addr.city === 'string' && addr.city.length > 0) out.city = addr.city
  if (typeof addr.state === 'string' && addr.state.length > 0) out.state = addr.state
  if (typeof addr.zipcode === 'string' && addr.zipcode.length > 0) out.postalCode = addr.zipcode
  if (typeof addr.region === 'string' && addr.region.length > 0) out.country = addr.region
  return out
}
